import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PageHeader from "../Layouts/PageHeader";
import { UiIcon } from "../Layouts/Icon";
import { ICON_COUNT_TEXT, ICON_LIST_PATH, COMPOSER_PATH, STUDIO_PATH, LICENSE_PATH } from "../constants";
import classes from "./about.scss";

const DESCRIPTION = `Download from our extensive collection of ${ICON_COUNT_TEXT} free, high-quality icons including duotone. Ideal for enhancing your designs, websites, mobile apps, and software.`;

// The story so far, and where it goes next
const CHAPTERS = [
  {
    when: "2015",
    title: "Free icons, for everyone",
    text: "IcoFont began as an icon font made for the community: icons anyone could use, free, in personal and commercial work alike.",
  },
  {
    when: "Today",
    title: "Your own font, your own icons",
    text: "The library has grown to line, solid and duotone. A faster Composer builds a font from only the icons you need, and the new Studio lets you design icons of your own.",
    links: [
      { to: COMPOSER_PATH, label: "Composer" },
      { to: STUDIO_PATH, label: "Studio" },
    ],
  },
  {
    when: "Next",
    title: "New sets, sharper icons",
    text: "We'll keep drawing new icon sets, and over time polish the icons already here, so the whole library keeps getting better.",
  },
];

// Opens in a new tab: an arrow out of a box
const NewTab = () => (
  <svg className={classes.newTab} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5M11.5 9.5v3a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// A link that opens in a new tab, and says so
const External = ({ href, children }) => (
  <a className={classes.ollyo} href={href} target="_blank" rel="noopener noreferrer">
    {children}
    <NewTab />
    <span className={classes.srOnly}> (opens in a new tab)</span>
  </a>
);

External.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export const About = () => (
  <div>
    <Helmet>
      <title>About | IcoFont</title>
      <meta name="description" content={DESCRIPTION} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://icofont.com/about" />
      <meta property="og:site_name" content="IcoFont" />
      <meta property="og:image" content="https://icofont.com/images/og/icons.png" />
      <meta name="og:title" content="About | IcoFont" />
      <meta name="og:description" content={DESCRIPTION} />
      <meta property="fb:app_id" content="101529056621542" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="IcoFont" />
      <meta name="twitter:description" content={DESCRIPTION} />
      <meta name="twitter:image:src" content="https://icofont.com/images/og/icons.png" />
    </Helmet>
    <PageHeader label="About" title="Meticulously crafted icons, free for everyone" />

    <div className={classes.page}>
      {/* Why IcoFont exists */}
      <section className={classes.opening}>
        <p className={classes.since}>Since 2015</p>
        <h2>Made for the community, free from the start.</h2>
        <p className={classes.lead}>
          IcoFont is a free icon font by <External href="https://ollyo.com/">Ollyo</External>, the
          team behind <External href="https://kirki.com/?utm_source=icofont&utm_medium=about">Kirki</External>,{" "}
          <External href="https://tutorlms.com/?utm_source=icofont&utm_medium=about">Tutor LMS</External> and{" "}
          <External href="https://www.joomshaper.com/?utm_source=icofont&utm_medium=about">JoomShaper</External>.
          We made it so that designers and developers
          everywhere could use good icons without a price tag or a license fee, in personal and
          commercial projects alike. That reason hasn't changed.
        </p>
      </section>

      {/* The story so far */}
      <section className={classes.section} aria-labelledby="about-story">
        <h2 id="about-story" className={classes.heading}>
          The story so far
        </h2>
        <ol className={classes.timeline}>
          {CHAPTERS.map((chapter) => (
            <li key={chapter.when}>
              <span className={classes.when}>{chapter.when}</span>
              <div className={classes.chapter}>
                <h3>{chapter.title}</h3>
                <p>{chapter.text}</p>
                {chapter.links ? (
                  <span className={classes.chapterLinks}>
                    {chapter.links.map((link) => (
                      <Link key={link.to} to={link.to}>
                        {link.label} <UiIcon name="arrow-right" />
                      </Link>
                    ))}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* The promise */}
      <section className={classes.promise}>
        <blockquote>
          <p>Icons for free and commercial use, for everyone. That's why IcoFont exists, and it's what we'll keep doing.</p>
        </blockquote>
        <div className={classes.promiseLinks}>
          <Link to={ICON_LIST_PATH} className={classes.primaryLight}>
            Browse {ICON_COUNT_TEXT} icons
          </Link>
          <Link to={LICENSE_PATH} className={classes.secondaryLight}>
            Read the license
          </Link>
        </div>
      </section>
    </div>
  </div>
);

export default About;
